/**
 * Linkify Service
 *
 * - Library: anchorme
 *
 * For converting text message to text message Display
 *
 * We need to display all original Text as pure string (including HTML tags (valid or invalid)).
 * From the original string, we try to "linkify" all existing URLs or links.
 * The library will not handle HTML tags so we actually need to handle it manually instead of straight up using the library for whole string.
 *
 * Full imeplementation of chatroom text message:
 * 1. Replace all htmlAttrs with a -TEMP postfix so that the attribute will not be skipped by the library.
 * 2. Find all links of the temp text using library (with {list: true}, which will return an array of links object)
 * 3. From the urls array, we loop through and add all original text (not link) as a string message part after reverse-replaced the htmlAttrs.
 * 4. For the url itself, we get a <a> tag using the library, and add it as a non-string message part.
 *
 * Created by Shan - 2018-05-09
 *
 */

import { Injectable } from '@angular/core';

import anchorme from 'anchorme';
import * as _ from 'lodash';
import { MessageMention } from '../../models/message';
import { TeamnoteConfigService } from '../../configs/teamnote-config.service';
import { strictEqual } from 'assert';
import { result } from 'lodash';


export const htmlAttrs: Array<string> = ['src=', 'data=', 'href=', 'cite=', 'formaction=', 'icon=', 'manifest=', 'poster=', 'codebase=', 'background=', 'profile=', 'usemap='];

/**
 * As we need to display HTML tags as normal string, but <a href></a> need to be a real HTML element, we need to separate the display of normal string and HTML
 *
 * @export
 * @class TextMessagePart
 */
export class TextMessagePart {
  isStr: boolean;
  text: any;
  isTranslateString?: boolean;
  isMention?: boolean;
  mentionUserId?: string;
  isHashtag?: boolean;

  constructor(isStr: boolean, text: any, isTranslateString?: boolean, isMention?: boolean, isHashtag?: boolean, mentionUserId?: string) {
    this.isStr = isStr;
    this.text = text;
    this.isTranslateString = isTranslateString;
    this.isMention = isMention;
    this.mentionUserId = mentionUserId;
    this.isHashtag = isHashtag;
  }
}

@Injectable()
export class LinkifyService {

  tempPostfix = '-TEMP';

  // tslint:disable-next-line:max-line-length
  hashtagRegEx = /#[^\r\n\t\f\v(#) \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]+/g; ///#(\w*[^\s(_).*(#)])/g; // /#([^\s]+)/;

  // punctuation the linkify library drops from the end of a url it detects, except for the
  // closing brackets below, which it keeps whenever the url itself opened them
  urlTrailingPunctuationRegEx = /[.,!?;:'">*~\-]/;
  urlTrailingBracketPairs = { ')': '(', ']': '[', '}': '{' };

  constructor(
    private _teamnoteConfigService: TeamnoteConfigService
  ) { }

  /**
   * Original pipe transform function
   * (not using)
   *
   * @param {string} str - original string
   * @returns {*} - linkified string
   * @memberof LinkifyService
   */
  transform(str: string): any {
    return str ? anchorme({
      input: str,
      options: {
        protocol: 'https://',
        attributes: {
          name: 'target',
          value: '_blank'
        }
      }
    }) : str;
  }

  /**
   * Get <a> string of the target url
   * - use library
   *
   * @param {string} url - target url
   * @returns {string} - <a> tag of url
   * @memberof LinkifyService
   */
  getATagUrl(url: string): string {
    return anchorme({
      input: url,
      options: {
        protocol: 'https://',
        attributes: {
          name: 'target',
          value: '_blank'
        }
      }
    });
  }

  /**
   * Get the <a> string of a url the library cannot linkify in full
   *
   * Running the library on such a url would linkify its head only and leave the rest as
   * plain text (see expandUrlTrailingFragments), so build the tag directly. No target
   * attribute is set here, to match what the library's anchors actually render as.
   *
   * @param {string} url - target url, already complete
   * @returns {string} - <a> tag of url
   * @memberof LinkifyService
   */
  getATagForExpandedUrl(url: string): string {
    const href = /^[a-z][a-z0-9+.\-]*:\/\//i.test(url) ? url : 'https://' + url;
    return '<a href="' + this.escapeHtml(href) + '">' + this.escapeHtml(url) + '</a>';
  }

  /**
   * Escape the characters that would otherwise break out of an attribute value or of text
   * content, as the urls below are inserted into a raw HTML string.
   *
   * @param {string} text - text to escape
   * @returns {string} - escaped text
   * @memberof LinkifyService
   */
  escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Get the full url of a url object reported by the library
   *
   * The library ends a url at its first "#" fragment, so a url carrying a second "#" -
   * e.g. "https://example.com/page#<anchor>#<state>" - is reported as a
   * short url followed by loose text, which then renders as a truncated link plus a
   * stray "#..." string. A "#" glued to the end of a url cannot start a hashtag, so
   * re-absorb everything up to the next whitespace, minus the trailing punctuation the
   * library itself would have dropped.
   *
   * @param {string} text - the whole text the url was found in
   * @param {*} url - url object from listUrls()
   * @returns {string} - the url including any fragment the library left out
   * @memberof LinkifyService
   */
  expandUrlTrailingFragments(text: string, url: any): string {
    // listUrls() also reports emails and file paths, which have no fragment to re-absorb
    if (!url.isURL) {
      return url.string;
    }
    if (text.charAt(url.end) !== '#') {
      return url.string;
    }
    let end = url.end;
    while (end < text.length && !/\s/.test(text.charAt(end))) {
      end++;
    }
    return this.trimUrlTrailingPunctuation(text.substring(url.start, end));
  }

  /**
   * Drop the trailing punctuation the library would have dropped itself
   *
   * A closing bracket is kept when the url opened it, so that an anchor such as
   * ".../wiki/Foo_(bar)" survives - the library balances brackets the same way, and a url
   * must not linkify differently just because it carries a second "#" fragment.
   *
   * @param {string} url - url to trim
   * @returns {string} - url without its trailing punctuation
   * @memberof LinkifyService
   */
  trimUrlTrailingPunctuation(url: string): string {
    let end = url.length;
    while (end > 0) {
      const char = url.charAt(end - 1);
      const opener = this.urlTrailingBracketPairs[char];
      if (opener) {
        const candidate = url.substring(0, end);
        if (this.countOccurrences(candidate, opener) >= this.countOccurrences(candidate, char)) {
          break;
        }
      } else if (!this.urlTrailingPunctuationRegEx.test(char)) {
        break;
      }
      end--;
    }
    return url.substring(0, end);
  }

  /**
   * Count how many times a single character occurs in a text
   *
   * @param {string} text - text to search
   * @param {string} char - character to count
   * @returns {number} - number of occurrences
   * @memberof LinkifyService
   */
  countOccurrences(text: string, char: string): number {
    let count = 0;
    for (let i = 0; i < text.length; i++) {
      if (text.charAt(i) === char) {
        count++;
      }
    }
    return count;
  }

  /**
   * Get a list of url inside text
   * - use library
   *
   * @param {string} text - text containing urls
   * @returns {any[]} - url object array
   * @memberof LinkifyService
   */
  listUrls(text: string): any[] {
    const list = anchorme.list(text);
    return list;
  }

  /**
   * Get the temp value of html attr (for replacing)
   *
   * @param {string} attr - html attr
   * @returns {string} - html attr + temp postfix
   * @memberof LinkifyService
   */
  getTempValForAttr(attr: string): string {
    return attr + this.tempPostfix;
  }

  /**
   * Replace all existences of htmlAttrs by its corresponding temp value
   * - use RegExp
   *
   * @param {string} text - text to be transformed
   * @returns {string} - transformed string
   * @memberof LinkifyService
   */
  tempReplaceAllHtmlAttr(text: string): string {
    _.each(htmlAttrs, (attr) => {
      const pattern = new RegExp('(' + attr + ')', 'g');
      text = text.replace(pattern, this.getTempValForAttr(attr));
    });
    return text;
  }

  /**
   * Replace all existences of temp htmlAttrs by its original htmlAttr
   *
   * @param {string} text - text to be recovered
   * @returns {string} - recovered text
   * @memberof LinkifyService
   */
  tempReverseAllHtmlAttr(text: string): string {
    _.each(htmlAttrs, (attr) => {
      text = text.replace(new RegExp('(' + this.getTempValForAttr(attr) + ')', 'g'), attr);
    });
    return text;
  }

  /**
   * Get a new TextMessagePart
   *
   * @param {boolean} isStr - is the content normal string?
   * @param {string} text - content
   * @returns {TextMessagePart} - new message part object
   * @memberof LinkifyService
   */
  // tslint:disable-next-line:max-line-length
  getMessagePart(isStr: boolean, text: string, isNoNeedCheckMentionHashtag?: boolean, mentionReplacer?: MessageMention[], segmentStartIndex?: number, segmentEndIndex?: number, precedingChar?: string): TextMessagePart[] {

    if (isNoNeedCheckMentionHashtag) {
      return [new TextMessagePart(isStr, text)];
    }

    let allToReplaceMapping: {index: number, type: string, text: string, mappedText: string, userId?: string, segmentIndex?: number}[] = [];
    const cloneText = _.clone(text);
    // find all mention
    if (mentionReplacer) {
      let pushedMappingIndex = []
      // console.warn(mentionReplacer);
      mentionReplacer.sort((a, b) => {
        return a.location - b.location;
      });
      _.each(mentionReplacer, (mention) => {
        if (text.indexOf(mention.name) !== -1) {
          if (mention.location >= segmentStartIndex && mention.location < segmentEndIndex) {
            if (_.indexOf(pushedMappingIndex, mention.location) !== -1) {
              return
            }

            allToReplaceMapping.push({
              // location is an index into the whole message, but the mapping below is sorted
              // against hashtag indexes, which are local to this segment
              index: mention.location - segmentStartIndex,
              type: '@',
              text: '@' + mention.name,
              mappedText: '@' + mention.name,
              userId: mention.user_id
            });
            pushedMappingIndex.push(mention.location);
          }
        }
      });
    }
    // find all hashtag
    if (this._teamnoteConfigService.config.WEBCLIENT.CHATROOM.IS_ENABLE_SEARCH) {
      let result;
      // end index of the previously accepted hashtag, so that "#one#two" stays two hashtags
      let prevHashtagEndIndex = -1;
      while (( result = this.hashtagRegEx.exec(cloneText)) !== null) {
        // A hashtag may only start the message, follow a whitespace / opening bracket, or
        // directly follow another hashtag. This segment is only a part of the original
        // message, so for a match at index 0 the preceding character has to be supplied by
        // the caller. Without it, the tail of a URL that the linkify library cut short -
        // e.g. the second "#..." fragment of "https://example.com/page#<anchor>#<state>"
        // - arrives here as its own segment starting with '#' and becomes a clickable hashtag.
        const charBeforeHashtag = result.index === 0 ? precedingChar : cloneText.charAt(result.index - 1);
        const isFollowingHashtag = result.index === prevHashtagEndIndex;
        if (!isFollowingHashtag && charBeforeHashtag && !/[\s(]/.test(charBeforeHashtag)) {
          continue;
        }
        prevHashtagEndIndex = result.index + result[0].length;
        allToReplaceMapping.push({
          index: result.index,
          type: '#',
          text: result[0],
          mappedText: result[0],
          // the index the match was actually found at, so that a rejected occurrence of the
          // same text earlier in the segment cannot be highlighted in its place
          segmentIndex: result.index
        });
      }
    }
    allToReplaceMapping = _.sortBy(allToReplaceMapping, 'index');
    // const results = [];
    let results = [];
    let textIndex = 0;
    if (allToReplaceMapping) {
      for (let i = 0; i < allToReplaceMapping.length; i++) {
        const mapping = allToReplaceMapping[i];
        const toReplace = mapping.text;
        // hashtags carry the index they matched at; a mention only knows roughly where it
        // starts - location points at the name, not at the '@' - so it is still searched for
        const toReplaceIndex = mapping.segmentIndex !== undefined
          ? mapping.segmentIndex
          : text.indexOf(toReplace, textIndex);

        if (toReplaceIndex < textIndex) {
          // overlaps something already emitted
          continue;
        }

        const prevText = text.substr(textIndex, toReplaceIndex - textIndex);
        if (prevText.length > 0) {
          if (this._teamnoteConfigService.config.WEBCLIENT.CHATROOM.IS_ENABLE_MARKDOWN_INPUT) {
            const allMarkdownFragments = this.findAllMarkDownMatches(prevText);
            if (allMarkdownFragments.length) {
              const markdownTextParts = this.getMarkdownMsgPart(allMarkdownFragments, prevText);
              // console.log('markdownTextParts', markdownTextParts);
              results = _.union(results, markdownTextParts);
            } else {
              results.push(new TextMessagePart(isStr, prevText));
            }

          } else {
            results.push(new TextMessagePart(isStr, prevText));
          }

          // results.push(new TextMessagePart(isStr, prevText));
        }

        // add mention / hashtag
        results.push(new TextMessagePart(isStr, toReplace, false, mapping.type === '@', mapping.type === '#', mapping.userId));

        // update textIndex pointer
        textIndex = toReplaceIndex + toReplace.length;
      }
    }
    // add remaining text
    // const remainingText = text.substr(textIndex);
    let remainingText = text.substr(textIndex);
    // console.log('remainingText', remainingText);
    if (remainingText.length > 0) {
      if (this._teamnoteConfigService.config.WEBCLIENT.CHATROOM.IS_ENABLE_MARKDOWN_INPUT) {
        const allMarkdownFragments = this.findAllMarkDownMatches(remainingText);
        if (allMarkdownFragments.length) {
  
          const markdownTextParts = this.getMarkdownMsgPart(allMarkdownFragments, remainingText);
          results = _.union(results, markdownTextParts);
        } else {
          results.push(new TextMessagePart(isStr, remainingText));
        }
      } else {
        results.push(new TextMessagePart(isStr, remainingText));
      }
      // const allMarkdownFragments = this.findAllMarkDownMatches(remainingText);
      // console.log('allMarkdownFragments', allMarkdownFragments);

      // if (allMarkdownFragments.length) {

      //   const markdownTextParts = this.getMarkdownMsgPart(allMarkdownFragments, remainingText);
      //   // console.log('markdownTextParts', markdownTextParts);
      //   results = _.union(results, markdownTextParts);
      // } else {
      //   results.push(new TextMessagePart(isStr, remainingText));
      // }
    }

    return results;
  }

  getMarkdownMsgPart(allMarkdownFragments: string[], remainingText: string): TextMessagePart[] {
    const results = [];

    let remainingTextIndex = 0;
    for (let i = 0; i < allMarkdownFragments.length; i++) {
      const markdown = allMarkdownFragments[i];
      const markdownIndex = remainingText.indexOf(markdown, remainingTextIndex);
      const prevText = remainingText.substr(remainingTextIndex, markdownIndex - remainingTextIndex);

      if (prevText.length > 0) {
        results.push(new TextMessagePart(true, prevText));
      }
      
      const mdText = this.parseMarkdownToHtmlContent(markdown);
      results.push(new TextMessagePart(false, mdText)); // isStr: false => is HTML content

      remainingTextIndex = markdownIndex + markdown.length;
    }

    const leftText = remainingText.substr(remainingTextIndex);
    // console.log('leftText', leftText);
    if (leftText.length > 0) {
      results.push(new TextMessagePart(true, leftText));
    }

    return results;
  }

  findAllMarkDownMatches(text: string): string[] {
    // const strikethroughRegex = /\~{1}([^\s][^\~]*[^\s]|[^\s]{1})\~{1}/gm;
    // const boldRegex = /\*{1}([^\s][^\*]*[^\s]|[^\s]{1})\*{1}/gm;
    // const underlineRegex = /\_{1}([^\s][^\_]*[^\s]|[^\s]{1})\_{1}/gm;
    // const combinedRegex = /(\~{1}([^\s][^\~]*[^\s]|[^\s]{1})\~{1}|\*{1}([^\s][^\*]*[^\s]|[^\s]{1})\*{1}|\_{1}([^\s][^\_]*[^\s]|[^\s]{1})\_{1})/gm;
    // const matches = text.match(combinedRegex) || [];

    
    // const combinedRegex = /(\~{1}([^\s][^\~]*[^\s]|[^\s]{1})\~{1}|\*{1}([^\s][^\*]*[^\s]|[^\s]{1})\*{1}|\_{1}([^\s][^\_]*[^\s]|[^\s]{1})\_{1})/gm;
    const combinedRegex = /(?<!\S)(\~{1}([^\s][^\~]*[^\s]|[^\s]{1})\~{1}|\*{1}([^\s][^\*]*[^\s]|[^\s]{1})\*{1}|\_{1}([^\s][^\_]*[^\s]|[^\s]{1})\_{1})(?!\S)/gm;
    const matches = text.match(combinedRegex);

    return matches?.length > 0 ? matches : [];
  }

  parseMarkdownToHtmlContent(mdText: string): string {
    return (
      mdText
        .replace(/\~{1}([^\s][^\~\n]*[^\s]|[^\s]{1})\~{1}/g, '<del>$1</del>') // strikethrough
        .replace(/\*{1}([^\s][^\*\n]*[^\s]|[^\s]{1})\*{1}/g, '<strong class="strong-content">$1</strong>') // bold
        .replace(/\_{1}([^\s][^\_\n]*[^\s]|[^\s]{1})\_{1}/g, '<em>$1</em>') // italic
        // .replace(/(?<!\S)\~{1}([^\s][^\~]*[^\s]|[^\s]{1})\~{1}(?!\S)/gm, '<del>$1</del>') // strikethrough
        // .replace(/(?<!\S)\*{1}([^\s][^\*]*[^\s]|[^\s]{1})\*{1}(?!\S)/gm, '<strong>$1</strong>') // bold
        // .replace(/(?<!\S)\_{1}([^\s][^\_]*[^\s]|[^\s]{1})\_{1}(?!\S)/gm, '<em>$1</em></em>') // italic
    );
  }

  /**
   * Parse the text message for displaying
   *
   * For full implementation logic, see the top comment of this file.
   *
   * @param {string} text - original text message
   * @returns {TextMessagePart[]} - Parsed message part array
   * @memberof LinkifyService
   */
  parseTextMessageForDisplay(text: string, mentionReplacer?: MessageMention[]): TextMessagePart[] {
    // replace all html attr first
    const temp = this.tempReplaceAllHtmlAttr(text);

    let parsedArr: TextMessagePart[] = [];

    // Get all urls in the text message
    const allUrls = this.listUrls(temp);

    if (!allUrls || allUrls.length === 0) {
      // No url, just string! add the original text
      parsedArr = _.union(parsedArr, this.getMessagePart(true, text, false, mentionReplacer, 0, temp.length, ''));
    } else {
      let textIndex = 0;
      for (let i = 0; i < allUrls.length; i++) {
        if (allUrls[i].start < textIndex) {
          // already covered by the previous expanded url
          continue;
        }

        const url = this.expandUrlTrailingFragments(temp, allUrls[i]);
        const isExpandedUrl = url !== allUrls[i].string;
        const urlIndex = temp.indexOf(url, textIndex);

        const prevText = temp.substr(textIndex, urlIndex - textIndex);
        if (prevText.length > 0) {
          // add prev text as string
          parsedArr = _.union(parsedArr, this.getMessagePart(true, this.tempReverseAllHtmlAttr(prevText), false, mentionReplacer, textIndex, urlIndex, temp.charAt(textIndex - 1)));
        }

        // Add url!
        // url is a slice of the html-attr-tempified text, so a url carrying a query param
        // named after one of the htmlAttrs (?src=, ?href=, ...) still holds the -TEMP
        // sentinel; the anchor has to show and point at the original
        const cleanUrl = this.tempReverseAllHtmlAttr(url);
        const aTag = isExpandedUrl ? this.getATagForExpandedUrl(cleanUrl) : this.getATagUrl(cleanUrl);
        parsedArr = _.union(parsedArr, this.getMessagePart(false, aTag, true));

        // update textIndex pointer
        textIndex = urlIndex + url.length;
      }
      // Add remaining text
      const reaminingText = temp.substr(textIndex);
      if (reaminingText.length > 0) {
        // add as string
        parsedArr = _.union(parsedArr, this.getMessagePart(true, this.tempReverseAllHtmlAttr(reaminingText), false, mentionReplacer, textIndex, temp.length, temp.charAt(textIndex - 1)));
      }
    }

    return parsedArr;
  }

  // trans string message to string array divided by substring(keyword) fragment 
  getMessageFragments(messageContent: string, keyword: string): string[] {
    const result = [];
    let startIndex = 0;
    let foundIndex
  
    // while ((foundIndex = messageContent.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase(), startIndex)) !== -1) {

    // }

    do {
      foundIndex = messageContent.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase(), startIndex)

      if (foundIndex === -1) {
        break;
      }

      if (foundIndex > startIndex) {
        result.push({ text: messageContent.substring(startIndex, foundIndex), isKeyword: false });
      }

      let nextStartIndex = foundIndex + keyword.length;
      result.push({ text: messageContent.substring(foundIndex, nextStartIndex), isKeyword: true });
      startIndex = nextStartIndex;
      
    } while (true);
  
    if (startIndex < messageContent.length) {
      result.push({ text: messageContent.substring(startIndex), isKeyword: false });
    }
  
    return result;
  }

}
